import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { PhoneInput } from "../components/ui/phone-input";
import { toast } from "sonner";
import axios from "axios";
import { 
  Stethoscope, 
  Map, 
  Users, 
  ClipboardCheck, 
  MessageCircle, 
  Target, 
  ArrowRight,
  Building2,
  GraduationCap,
  CheckCircle,
  ChevronRight,
  Send
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    organization: "",
    role: "university",
    phone: "",
    message: ""
  });
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/demo-requests`, formData);
      toast.success("Demo request submitted! We'll contact you soon.");
      setShowDemoForm(false);
      setFormData({ name: "", email: "", organization: "", role: "university", phone: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Map,
      title: "Interactive Site Mapping",
      description: "Visualize all clinical sites on an interactive map with real-time availability data."
    },
    {
      icon: Users,
      title: "Student Management",
      description: "Comprehensive student tracking with placement status, preferences, and distance matching."
    },
    {
      icon: ClipboardCheck,
      title: "Streamlined Approvals",
      description: "Multi-step approval workflow with accept, counter-offer, and deny capabilities."
    },
    {
      icon: MessageCircle,
      title: "AI Assistant (Sam)",
      description: "Intelligent AI assistant for matching students to rotations and answering questions."
    },
    {
      icon: Target,
      title: "Smart Match",
      description: "AI-powered matching with real driving distance calculations to find optimal placements."
    },
    {
      icon: Building2,
      title: "Multi-Organization",
      description: "Support for multiple universities and clinical sites in one unified platform."
    }
  ];

  const benefits = [
    "Reduce placement coordination time by 70%",
    "Real-time visibility across all stakeholders",
    "Automated distance-based matching",
    "Comprehensive reporting and analytics",
    "Secure HIPAA-compliant infrastructure"
  ];

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      {/* Navigation */}
      <nav
        data-testid="landing-nav"
        className={`fixed top-0 w-full z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-300 ${
          scrolled ? "bg-[#0B0E14]/90 backdrop-blur-md border-slate-800" : "bg-transparent border-transparent"
        }`}
      >
        <div className={`max-w-7xl mx-auto px-6 py-4 flex items-center justify-between ${scrolled ? "" : "[text-shadow:0_1px_6px_rgba(0,0,0,0.7)]"}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-400/10 rounded-xl flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-cyan-400" />
            </div>
            <span className="text-xl font-bold text-white">Precepta</span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-white hover:text-white hover:bg-white/10"
              onClick={() => setShowDemoForm(true)}
              data-testid="nav-request-demo"
            >
              Request Demo
            </Button>
            <Button 
              className="bg-transparent text-white border border-white hover:bg-white/10 font-semibold"
              onClick={() => navigate("/login")}
              data-testid="nav-login"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Full viewport height, responsive */}
      <section className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 sm:px-6">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            className="absolute w-full h-full object-cover"
            style={{ 
              objectPosition: 'center center',
              minWidth: '100%',
              minHeight: '100%'
            }}
            src="/videos/hero.mp4"
            poster="/videos/hero-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            data-testid="hero-video"
          />
          {/* Bottom gradient fade to page background */}
          <div className="absolute inset-x-0 bottom-0 h-24 sm:h-32 bg-gradient-to-b from-transparent to-[#0B0E14]" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto pt-20 pb-16 sm:pt-24 sm:pb-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight [text-shadow:0_2px_12px_rgba(0,0,0,0.5)]">
              Streamline Clinical
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
                Placements
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white mb-8 sm:mb-10 [text-shadow:0_1px_8px_rgba(0,0,0,0.6)] max-w-2xl mx-auto px-4">
              Connect universities with clinical sites seamlessly. Manage rotations, track students, 
              and automate approvals with AI-powered intelligence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Button 
                size="lg"
                className="bg-transparent text-white border border-white hover:bg-white/10 font-semibold text-base sm:text-lg px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-52"
                onClick={() => setShowDemoForm(true)}
                data-testid="hero-request-demo"
              >
                Request a Demo
                <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="bg-transparent text-white border border-white hover:bg-white/10 hover:text-white font-semibold text-base sm:text-lg px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-52"
                onClick={() => navigate("/login")}
                data-testid="hero-login"
              >
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 border-y border-slate-800 bg-[#151A23]/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">800+</div>
              <div className="text-slate-400">Clinical Sites</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">50+</div>
              <div className="text-slate-400">Universities</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">10k+</div>
              <div className="text-slate-400">Students Placed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">99%</div>
              <div className="text-slate-400">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Need for Clinical Placements
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              A comprehensive platform designed specifically for healthcare education coordination.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-[#151A23] border-slate-800 hover:border-cyan-400/50 transition-all duration-300 group">
                <CardHeader>
                  <div className="w-12 h-12 bg-cyan-400/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-cyan-400/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <CardTitle className="text-white text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-400">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* For Who Section */}
      <section className="py-20 px-6 bg-[#151A23]/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* For Universities */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-transparent border border-cyan-500/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-cyan-400/20 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">For Universities</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "Manage student cohorts and placements",
                  "Submit placement requests to clinical sites",
                  "Track approval status in real-time",
                  "Distance-based site recommendations",
                  "Comprehensive reporting and analytics"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* For Clinical Sites */}
            <div className="p-8 rounded-2xl bg-gradient-to-br from-violet-500/10 to-transparent border border-violet-500/20">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-violet-400/20 rounded-xl flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-violet-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">For Clinical Sites</h3>
              </div>
              <ul className="space-y-4">
                {[
                  "Create and manage rotation slots",
                  "Review incoming placement requests",
                  "Accept, counter, or deny requests",
                  "Track student assignments",
                  "Manage site capacity and availability"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-violet-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Placement Process?
          </h2>
          <p className="text-lg text-slate-400 mb-10">
            Join leading healthcare education institutions using Precepta to streamline clinical placements.
          </p>
          <Button 
            size="lg"
            className="bg-cyan-400 text-black hover:bg-cyan-300 font-semibold text-lg px-10 h-14 shadow-lg shadow-cyan-500/25"
            onClick={() => setShowDemoForm(true)}
            data-testid="cta-request-demo"
          >
            Request a Demo Today
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-cyan-400/10 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="font-semibold text-white">Precepta</span>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Precepta Clinical Placement System. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Demo Request Modal */}
      {showDemoForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-lg bg-[#151A23] border-slate-700" data-testid="demo-request-modal">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white text-xl">Request a Demo</CardTitle>
                  <CardDescription className="text-slate-400 mt-1">
                    Fill out the form and we'll contact you to schedule a demo.
                  </CardDescription>
                </div>
                <button 
                  onClick={() => setShowDemoForm(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleDemoSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      data-testid="demo-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-300">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      data-testid="demo-email-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="organization" className="text-slate-300">Organization *</Label>
                    <Input
                      id="organization"
                      value={formData.organization}
                      onChange={(e) => setFormData({...formData, organization: e.target.value})}
                      required
                      className="bg-slate-800/50 border-slate-700 text-slate-200"
                      data-testid="demo-org-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-slate-300">I represent a *</Label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-slate-200 focus:outline-none focus:border-cyan-400"
                      data-testid="demo-role-select"
                    >
                      <option value="university">University</option>
                      <option value="clinical_site">Clinical Site / Hospital</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-300">Phone (optional)</Label>
                  <PhoneInput
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-slate-800/50 border-slate-700 text-slate-200"
                    data-testid="demo-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-300">Message (optional)</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={3}
                    placeholder="Tell us about your needs..."
                    className="bg-slate-800/50 border-slate-700 text-slate-200 placeholder:text-slate-500"
                    data-testid="demo-message-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-cyan-400 text-black hover:bg-cyan-300 font-semibold"
                  disabled={loading}
                  data-testid="demo-submit-btn"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5 mr-2" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Submit Request
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
